// @flow
import React from 'react';
import injectSheet from 'react-jss';
import isEmpty from 'lodash/isEmpty';
import { S } from '~/app/ui/tokens';
import EmptyState from '~/app/ui/EmptyState';
import Templates from 'shared/templates';
import cx from 'classnames';

export type $Template = {
  id: string,
  name: string,
  creator: string,
  updatedBy: string,
  defaultProps: Object,
  templateSettings: Object,
  createdAt: Date,
  updatedAt: Date
};

type Props = {
  classes: Object,
  template: $Template
};

function TemplateBrowserItem({ classes: c, template }: Props) {
  const TemplateComponent = Templates[template.id];
  const templateProps = { ...template.defaultProps };

  if (isEmpty(templateProps.slots)) {
    templateProps.slots = generateSlots(TemplateComponent);
  }
  // Blank starts with nothing in any area, so its preview would be an empty white frame.
  const startsEmpty = templateProps.slots.every(s => isEmpty(s.components));

  return (
    <div className={c.container}>
      <div className={c.header}>
        <h2 className={c.title}>
          {template.name}
        </h2>
      </div>
      <div className={cx(c.preview, 'template-browser-wrapper')} data-gx-theme="light">
        {startsEmpty
          ? <EmptyState icon="crop_free" title="An empty page" testId="template-preview-empty">No menu bar, footer or sections. You add what the page needs in the editor.</EmptyState>
          : <TemplateComponent {...templateProps} />}
      </div>
    </div>
  );
}

function generateSlots(Template) {
  const style = {
    border: '1px dashed hsl(187, 100%, 42%)',
    borderRadius: '3px',
    width: '100%',
    height: '100px',
    alignSelf: 'stretch',
    justifySelf: 'stretch',
    flex: '1 1 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '5px',
    margin: '15px'
  };

  return Template.schema.slots.map(s => ({
    style,
    slotId: s.slotId,
    components: [s.label]
  }));
}

export default injectSheet({
  container: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0
  },
  header: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '4px 16px',
    marginBottom: 10,
    fontFamily: S.ui
  },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 600,
    color: S.fg
  },
  // The template as it renders on the site, in a scrolling frame.
  preview: {
    color: 'hsl(0, 0%, 20%)',
    border: `1px solid ${S.line}`,
    overflow: 'auto',
    fontSize: '14px',
    background: 'hsl(0, 0%, 100%)',
    fontWeight: '300',
    borderRadius: 12,
    height: 'calc(100vh - 230px)',
    minHeight: 360
  }
})(TemplateBrowserItem);
